import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './GuidePages.css';
import GuideAdSlot from './GuideAdSlot';
import { GUIDE_SWAHILI_CONTENT, SwahiliSegment } from '../data/guideSwahiliContent';

const renderSegments = (segments: SwahiliSegment[]) =>
  segments.map((seg, i) => {
    if (seg.style === 'strong') return <strong key={i}>{seg.text}</strong>;
    if (seg.style === 'em') return <em key={i}>{seg.text}</em>;
    return <React.Fragment key={i}>{seg.text}</React.Fragment>;
  });

const GuideSwahili: React.FC = () => {
  const { i18n } = useTranslation();
  const c = GUIDE_SWAHILI_CONTENT[i18n.language === 'en' ? 'en' : 'fr'];

  useEffect(() => {
    document.title = c.pageTitleTag;
  }, [c.pageTitleTag]);

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 jg-guide-page">

      <nav style={{ marginBottom: '1.5rem', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
        <Link to="/" style={{ color: 'var(--color-accent)', textDecoration: 'none' }}>{c.breadcrumbHome}</Link>
        {' › '}
        <Link to="/about" style={{ color: 'var(--color-accent)', textDecoration: 'none' }}>{c.breadcrumbGuides}</Link>
        {' › '}
        <span>{c.breadcrumbCurrent}</span>
      </nav>

      <h1 className="text-3xl font-bold mb-4">
        {c.h1}
      </h1>
      <p style={{ color: 'var(--text-secondary)', marginBottom: '2rem', fontSize: '1.05rem' }}>
        {c.intro}
      </p>

      <div className="prose prose-lg max-w-none">

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">
            {c.whatIsTitle}
          </h2>
          <p className="mb-4 leading-relaxed" style={{ color: 'var(--text)' }}>
            {renderSegments(c.whatIsP1)}
          </p>
          <p className="mb-4 leading-relaxed" style={{ color: 'var(--text)' }}>
            {renderSegments(c.whatIsP2)}
          </p>
          <p className="mb-4 leading-relaxed" style={{ color: 'var(--text)' }}>
            {renderSegments(c.whatIsP3)}
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">
            {c.accessibleTitle}
          </h2>
          <p className="mb-4 leading-relaxed" style={{ color: 'var(--text)' }}>
            {c.accessibleIntro}
          </p>
          <ul style={{ paddingLeft: '1.5rem', marginBottom: '1rem', color: 'var(--text)' }}>
            {c.accessibleList.map((item, i) => (
              <li key={i} style={{ marginBottom: '0.5rem' }}>
                {renderSegments(item)}
              </li>
            ))}
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">
            {c.nounClassesTitle}
          </h2>
          <p className="mb-4 leading-relaxed" style={{ color: 'var(--text)' }}>
            {renderSegments(c.nounClassesP1)}
          </p>
          <p className="mb-4 leading-relaxed" style={{ color: 'var(--text)' }}>
            {c.nounClassesIntro}
          </p>
          <ul style={{ paddingLeft: '1.5rem', marginBottom: '1rem', color: 'var(--text)' }}>
            {c.nounClassesList.map((item, i) => (
              <li key={i} style={{ marginBottom: '0.5rem' }}>
                {renderSegments(item)}
              </li>
            ))}
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">
            {c.greetingsTitle}
          </h2>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '1.5rem' }}>
              <thead>
                <tr style={{ background: 'var(--color-accent)', color: '#fff' }}>
                  <th style={{ padding: '10px 14px', textAlign: 'left' }}>{c.greetingsTableHeaders.gloss}</th>
                  <th style={{ padding: '10px 14px', textAlign: 'left' }}>{c.greetingsTableHeaders.sw}</th>
                  <th style={{ padding: '10px 14px', textAlign: 'left' }}>{c.greetingsTableHeaders.pron}</th>
                </tr>
              </thead>
              <tbody>
                {c.greetingsRows.map((row) => (
                  <tr key={row.gloss} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '8px 14px', color: 'var(--text)' }}>{row.gloss}</td>
                    <td style={{ padding: '8px 14px', fontWeight: 600, color: 'var(--color-accent)' }}>{row.sw}</td>
                    <td style={{ padding: '8px 14px', color: 'var(--text-secondary)', fontStyle: 'italic' }}>{row.pron}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">
            {c.vocabTitle}
          </h2>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '1.5rem' }}>
              <thead>
                <tr style={{ background: 'var(--color-accent)', color: '#fff' }}>
                  <th style={{ padding: '10px 14px', textAlign: 'left' }}>{c.vocabTableHeaders.gloss}</th>
                  <th style={{ padding: '10px 14px', textAlign: 'left' }}>{c.vocabTableHeaders.sw}</th>
                </tr>
              </thead>
              <tbody>
                {c.vocabRows.map((row) => (
                  <tr key={row.gloss} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '8px 14px', color: 'var(--text)' }}>{row.gloss}</td>
                    <td style={{ padding: '8px 14px', fontWeight: 600, color: 'var(--color-accent)' }}>{row.sw}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">
            {c.cultureTitle}
          </h2>
          <p className="mb-4 leading-relaxed" style={{ color: 'var(--text)' }}>
            {renderSegments(c.cultureP1)}
          </p>
          <p className="mb-4 leading-relaxed" style={{ color: 'var(--text)' }}>
            {renderSegments(c.cultureP2)}
          </p>
          <p className="mb-4 leading-relaxed" style={{ color: 'var(--text)' }}>
            {renderSegments(c.cultureP3)}
          </p>
        </section>

        <section className="mb-8 jg-guide-callout" style={{ background: 'var(--surface-raised)', borderRadius: '12px', padding: '1.5rem' }}>
          <h2 className="text-2xl font-semibold mb-4">
            {c.ctaTitle}
          </h2>
          <p className="mb-4 leading-relaxed" style={{ color: 'var(--text)' }}>
            {c.ctaText}
          </p>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <Link
              to="/"
              style={{
                background: 'var(--color-accent)', color: '#fff', padding: '0.75rem 1.5rem',
                borderRadius: '10px', textDecoration: 'none', fontWeight: 700, display: 'inline-block'
              }}
            >
              {c.ctaButtonStart}
            </Link>
            <Link
              to="/guide/wolof"
              style={{
                background: 'transparent', color: 'var(--color-accent)', padding: '0.75rem 1.5rem',
                borderRadius: '10px', textDecoration: 'none', fontWeight: 600,
                border: '1.5px solid var(--color-accent)', display: 'inline-block'
              }}
            >
              {c.ctaButtonWolof}
            </Link>
            <Link
              to="/guide/bambara"
              style={{
                background: 'transparent', color: 'var(--color-accent)', padding: '0.75rem 1.5rem',
                borderRadius: '10px', textDecoration: 'none', fontWeight: 600,
                border: '1.5px solid var(--color-accent)', display: 'inline-block'
              }}
            >
              {c.ctaButtonBambara}
            </Link>
          </div>
        </section>

      </div>

      <GuideAdSlot />

      <footer style={{ marginTop: '3rem', paddingTop: '1.5rem', borderTop: '1px solid var(--border)', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
        <Link to="/" style={{ color: 'var(--color-accent)', marginRight: '1rem' }}>{c.footerHome}</Link>
        <Link to="/about" style={{ color: 'var(--color-accent)', marginRight: '1rem' }}>{c.footerAbout}</Link>
        <Link to="/guide/wolof" style={{ color: 'var(--color-accent)', marginRight: '1rem' }}>{c.footerWolof}</Link>
        <Link to="/guide/bambara" style={{ color: 'var(--color-accent)' }}>{c.footerBambara}</Link>
      </footer>
    </div>
  );
};

export default GuideSwahili;
